import { I18nLanguageEnum } from "@app/configs/i18n";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const SelectorContainer = styled.div<{ isMobile?: boolean }>`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 16px;
  width: 100%;

  @media (max-width: 768px) {
    gap: 6px;
    margin-bottom: 12px;
  }
`;

const LanguageButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 6px;
  padding: 10px 14px;
  background: ${(props) =>
    props.isActive
      ? "rgba(196, 145, 0, 0.15)"
      : "rgba(255, 255, 255, 0.9)"};
  backdrop-filter: blur(10px);
  border: 1px solid
    ${(props) =>
      props.isActive
        ? "rgba(196, 145, 0, 0.6)"
        : "rgba(0, 0, 0, 0.1)"};
  border-radius: 12px;
  color: #1a1a1a;
  font-size: 14px;
  font-weight: ${(props) => (props.isActive ? "600" : "500")};
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${(props) =>
    props.isActive
      ? "0 4px 15px rgba(196, 145, 0, 0.3)"
      : "0 2px 8px rgba(0, 0, 0, 0.08)"};

  &:hover {
    background: ${(props) =>
      props.isActive
        ? "rgba(196, 145, 0, 0.2)"
        : "rgba(255, 255, 255, 1)"};
    border-color: rgba(196, 145, 0, 0.4);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
    gap: 4px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    padding: 8px 10px;
    font-size: 11px;
    gap: 2px;
  }
`;

// Compact Flag Icon Style (for in-call display)
const CompactFlagContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

const CompactFlagButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(217, 119, 6, 0.1);
  border: 1px solid rgba(217, 119, 6, 0.3);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;

  &:hover {
    background: rgba(217, 119, 6, 0.2);
    border-color: rgba(217, 119, 6, 0.5);
  }
`;

const CompactDropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(217, 119, 6, 0.3);
  border-radius: 8px;
  overflow: hidden;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transform: ${(props) => (props.isOpen ? "translateY(0)" : "translateY(-5px)")};
  transition: all 0.2s ease;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const CompactDropdownItem = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: ${(props) => (props.isActive ? "rgba(217, 119, 6, 0.15)" : "transparent")};
  border: none;
  color: ${(props) => (props.isActive ? "#b45309" : "#451a03")};
  font-size: 13px;
  font-weight: ${(props) => (props.isActive ? "600" : "400")};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  white-space: nowrap;

  &:hover {
    background: rgba(217, 119, 6, 0.1);
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(217, 119, 6, 0.15);
  }
`;

// Mobile Dropdown Styles
const MobileDropdownContainer = styled.div`
  position: fixed;
  bottom: 30px;
  right: 16px;
  z-index: 9999;
`;

const MobileDropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: rgba(10, 10, 10, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(59, 130, 246, 0.4);
  border-radius: 8px;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(59, 130, 246, 0.8);
    background: rgba(59, 130, 246, 0.1);
  }

  svg {
    transition: transform 0.2s ease;
  }
`;

const MobileDropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  background: rgba(10, 10, 10, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(59, 130, 246, 0.5);
  border-radius: 10px;
  overflow: hidden;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transform: ${(props) => (props.isOpen ? "translateY(0)" : "translateY(10px)")};
  transition: all 0.2s ease;
`;

const MobileDropdownItem = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  background: ${(props) => (props.isActive ? "rgba(59, 130, 246, 0.2)" : "transparent")};
  border: none;
  color: ${(props) => (props.isActive ? "#60a5fa" : "#fff")};
  font-size: 14px;
  font-weight: ${(props) => (props.isActive ? "600" : "400")};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    background: rgba(59, 130, 246, 0.15);
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(59, 130, 246, 0.2);
  }
`;

const languages = [
  { code: I18nLanguageEnum.English, label: "English", flag: "🇬🇧" },
  { code: I18nLanguageEnum["Tiếng Việt"], label: "Tiếng Việt", flag: "🇻🇳" },
  { code: I18nLanguageEnum["ភាសាខ្មែរ"], label: "ភាសាខ្មែរ", flag: "🇰🇭" },
];

interface LanguageSelectorProps {
  onLanguageChange?: (languageCode: string) => void;
  isMobile?: boolean;
  compact?: boolean;
}

export const LanguageSelector = ({ onLanguageChange, isMobile, compact }: LanguageSelectorProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleLanguageChange = useCallback(
    (languageCode: string) => {
      i18n.changeLanguage(languageCode);
      setIsOpen(false);

      if (onLanguageChange) {
        onLanguageChange(languageCode);
      }
    },
    [i18n, onLanguageChange]
  );

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  // Compact: Small flag icon with dropdown
  if (compact) {
    return (
      <CompactFlagContainer>
        <CompactFlagButton onClick={() => setIsOpen(!isOpen)}>
          {currentLanguage.flag}
        </CompactFlagButton>
        <CompactDropdownMenu isOpen={isOpen}>
          {languages.map((lang) => (
            <CompactDropdownItem
              key={lang.code}
              isActive={lang.code === i18n.language}
              onClick={() => handleLanguageChange(lang.code)}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </CompactDropdownItem>
          ))}
        </CompactDropdownMenu>
      </CompactFlagContainer>
    );
  }

  // Mobile: Dropdown
  if (isMobile) {
    return (
      <MobileDropdownContainer>
        <MobileDropdownMenu isOpen={isOpen}>
          {languages.map((lang) => (
            <MobileDropdownItem
              key={lang.code}
              isActive={lang.code === i18n.language}
              onClick={() => handleLanguageChange(lang.code)}
            >
              <span style={{ fontSize: 18 }}>{lang.flag}</span>
              <span>{lang.label}</span>
            </MobileDropdownItem>
          ))}
        </MobileDropdownMenu>
        <MobileDropdownButton onClick={() => setIsOpen(!isOpen)}>
          <span style={{ fontSize: 14 }}>{currentLanguage.flag}</span>
          <span>{currentLanguage.label}</span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
          >
            <polyline points="18 15 12 9 6 15"></polyline>
          </svg>
        </MobileDropdownButton>
      </MobileDropdownContainer>
    );
  }

  // Desktop: Buttons
  return (
    <SelectorContainer>
      {languages.map((lang) => (
        <LanguageButton
          key={lang.code}
          isActive={lang.code === i18n.language}
          onClick={() => handleLanguageChange(lang.code)}
        >
          <span>{lang.flag}</span>
          <span>{lang.label}</span>
        </LanguageButton>
      ))}
    </SelectorContainer>
  );
};
